import { useState, useCallback } from "react";

const PLATFORMS = [
  { id: "instagram", label: "📸 Instagram", guide: "Instagram caption: 150-220 chars, emojis, 5-8 hashtags on new line", limit: 2200 },
  { id: "twitter",   label: "🐦 Twitter/X",  guide: "Twitter/X post: max 280 chars, punchy opener, max 2 hashtags", limit: 280 },
  { id: "whatsapp",  label: "💬 WhatsApp",   guide: "WhatsApp broadcast: 80-150 words, conversational, clear CTA", limit: 1000 },
  { id: "product",   label: "🏷️ Product Desc", guide: "Product description: 3-5 sentences, features + benefits + CTA", limit: 500 },
];
const TONES = [
  { id: "professional", label: "Professional" },
  { id: "casual",       label: "Casual & Fun" },
  { id: "urgent",       label: "Urgent / Promo" },
  { id: "luxury",       label: "Premium / Luxury" },
  { id: "bold",         label: "Bold & Hype" },
];
const TONE_GUIDES = {
  professional: "professional and trustworthy",
  casual:       "casual, friendly and fun",
  urgent:       "urgent, creates FOMO, drives immediate action",
  luxury:       "premium and aspirational, sophisticated",
  bold:         "bold, hype, high-energy like a street brand",
};
const IMG_STYLES = [
  { id: "realistic",    label: "📷 Realistic",    guide: "photorealistic, professional photography, 4k" },
  { id: "illustration", label: "🎨 Illustration",  guide: "digital illustration, vibrant colors, flat design" },
  { id: "minimal",      label: "⬜ Minimal",       guide: "minimalist, clean white background, simple shapes" },
  { id: "cinematic",    label: "🎬 Cinematic",     guide: "cinematic lighting, dramatic, film still, moody" },
];
const VIDEO_TYPES = [
  { id: "reels",     label: "🎬 Reels/TikTok",  dur: "30-60s" },
  { id: "youtube",   label: "▶️ YouTube Short", dur: "60-90s" },
  { id: "ad",        label: "📢 Promo Ad",       dur: "15-30s" },
  { id: "explainer", label: "💡 Explainer",      dur: "60-120s" },
];
const TREND_PLATFORMS = [
  { id: "twitter",   label: "🐦 Twitter/X" },
  { id: "instagram", label: "📸 Instagram" },
  { id: "tiktok",    label: "🎵 TikTok" },
  { id: "linkedin",  label: "💼 LinkedIn" },
];

const C = {
  accent:"#FF5C2B", dark:"#1A1A2E", muted:"#8A8070",
  border:"#E2D9C8", surface:"#FDFAF4", bg:"#F5F0E8",
  tag:"#EDE8DF", green:"#22c55e", red:"#ef4444", amber:"#F59E0B",
};

const inp = { width:"100%", background:C.surface, border:"1.5px solid "+C.border, borderRadius:10, padding:"10px 14px", fontFamily:"inherit", fontSize:"0.83rem", color:C.dark, outline:"none", boxSizing:"border-box" };
const slabel = { fontSize:"0.6rem", fontWeight:700, letterSpacing:"0.12em", textTransform:"uppercase", color:C.muted, marginBottom:8, display:"block" };
const bigBtn = (d) => ({ width:"100%", padding:13, borderRadius:12, background:C.accent, border:"none", color:"white", fontSize:"0.9rem", fontWeight:700, cursor:d?"not-allowed":"pointer", opacity:d?0.6:1, fontFamily:"inherit" });
const chip = (a) => ({ padding:"6px 12px", borderRadius:8, border:a?"1.5px solid "+C.accent:"1.5px solid "+C.border, background:a?C.accent:C.surface, color:a?"white":C.muted, cursor:"pointer", fontFamily:"inherit", fontSize:"0.72rem", fontWeight:500 });
const tone = (a) => ({ padding:"5px 12px", borderRadius:20, border:a?"1.5px solid "+C.dark:"1.5px solid "+C.border, background:a?C.dark:"transparent", color:a?"white":C.muted, cursor:"pointer", fontFamily:"inherit", fontSize:"0.7rem", fontWeight:500 });
const tabS = (a) => ({ flex:1, padding:"9px 4px", border:"none", background:a?C.accent:"transparent", color:a?"white":C.muted, cursor:"pointer", fontFamily:"inherit", fontSize:"0.66rem", fontWeight:700, borderRadius:7 });
const card = { background:C.surface, border:"1.5px solid "+C.border, borderRadius:12, padding:14, marginBottom:10 };

function Dots({text}) {
  return (
    <div style={{textAlign:"center",padding:"28px 0"}}>
      <div style={{display:"flex",justifyContent:"center",gap:6,marginBottom:10}}>
        {[0,1,2].map(i=><div key={i} style={{width:8,height:8,borderRadius:"50%",background:C.accent,animation:"bounce 1.2s "+(i*0.2)+"s infinite"}}/>)}
      </div>
      <div style={{fontSize:"0.74rem",color:C.muted}}>{text||"Generating..."}</div>
    </div>
  );
}
function ErrBox({msg}) {
  return <div style={{marginTop:12,background:"#fff8f6",border:"2px dashed #ffccbb",borderRadius:10,padding:12,color:"#cc4400",fontSize:"0.76rem",textAlign:"center"}}>⚠️ {msg}</div>;
}
function CharCount({text,limit}) {
  const len=(text||"").length, over=len>limit, pct=Math.min((len/limit)*100,100);
  return (
    <div style={{marginTop:4,display:"flex",alignItems:"center",gap:8}}>
      <div style={{flex:1,height:3,background:C.border,borderRadius:3,overflow:"hidden"}}>
        <div style={{width:pct+"%",height:"100%",background:over?C.red:len>limit*0.8?C.amber:C.green,borderRadius:3,transition:"width 0.2s"}}/>
      </div>
      <span style={{fontSize:"0.6rem",color:over?C.red:C.muted,fontWeight:over?700:400}}>{len}/{limit}</span>
    </div>
  );
}

const callClaude = async (prompt, maxTokens=1200) => {
  const res = await fetch("https://api.anthropic.com/v1/messages", {
    method:"POST",
    headers:{"Content-Type":"application/json","anthropic-version":"2023-06-01","anthropic-dangerous-direct-browser-access":"true"},
    body:JSON.stringify({model:"claude-sonnet-4-20250514",max_tokens:maxTokens,messages:[{role:"user",content:prompt}]}),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error?.message||"API error "+res.status);
  return data.content?.[0]?.text||"";
};
const parseJ = (raw) => JSON.parse(raw.replace(/```json|```/g,"").trim());

export default function ContentAIStudio() {
  const [tab,setTab]=useState("copy");
  const [bizName,setBizName]=useState("");
  const [bizNiche,setBizNiche]=useState("");
  const [bizOffer,setBizOffer]=useState("");

  const [platform,setPlatform]=useState("instagram");
  const [tn,setTn]=useState("professional");
  const [copyLoading,setCopyLoading]=useState(false);
  const [copyResults,setCopyResults]=useState([]);
  const [copyError,setCopyError]=useState("");
  const [copied,setCopied]=useState(null);
  const [history,setHistory]=useState([]);
  const [showHistory,setShowHistory]=useState(false);
  const [repLoading,setRepLoading]=useState(false);
  const [repSource,setRepSource]=useState(null);
  const [repResults,setRepResults]=useState([]);
  const [repError,setRepError]=useState("");

  const [imgStyle,setImgStyle]=useState("realistic");
  const [imgPrompt,setImgPrompt]=useState("");
  const [imgLoading,setImgLoading]=useState(false);
  const [sugLoading,setSugLoading]=useState(false);
  const [images,setImages]=useState([]);
  const [imgLoaded,setImgLoaded]=useState([]);
  const [imgError,setImgError]=useState("");

  const [videoType,setVideoType]=useState("reels");
  const [videoTone,setVideoTone]=useState("casual");
  const [videoLoading,setVideoLoading]=useState(false);
  const [videoScript,setVideoScript]=useState(null);
  const [videoError,setVideoError]=useState("");
  const [scriptCopied,setScriptCopied]=useState(false);

  const [trendPlatform,setTrendPlatform]=useState("instagram");
  const [trendLoading,setTrendLoading]=useState(false);
  const [trendData,setTrendData]=useState(null);
  const [trendError,setTrendError]=useState("");
  const [trendCL,setTrendCL]=useState(null);
  const [trendContent,setTrendContent]=useState({});
  const [trendCopied,setTrendCopied]=useState(null);

  const [calLoading,setCalLoading]=useState(false);
  const [calendar,setCalendar]=useState(null);
  const [calError,setCalError]=useState("");
  const [calCopied,setCalCopied]=useState(null);

  const [compPost,setCompPost]=useState("");
  const [compLoading,setCompLoading]=useState(false);
  const [compResults,setCompResults]=useState(null);
  const [compError,setCompError]=useState("");
  const [compCopied,setCompCopied]=useState(null);

  const cp = (key,text) => { navigator.clipboard.writeText(text); setCopied(key); setTimeout(()=>setCopied(null),2000); };

  const generateCopy = async () => {
    if (!bizName.trim()||!bizOffer.trim()) { setCopyError("Fill in Business Name and Product/Service."); return; }
    setCopyError(""); setCopyLoading(true); setCopyResults([]);
    const p=PLATFORMS.find(x=>x.id===platform);
    try {
      const raw=await callClaude("You are an expert social media copywriter.\nBusiness: "+bizName+" | Industry: "+(bizNiche||"General")+" | Offer: "+bizOffer+"\nPlatform: "+p.guide+" | Tone: "+TONE_GUIDES[tn]+"\nGenerate exactly 3 unique variations. Return ONLY valid JSON array, no markdown:\n[{\"variation\":1,\"content\":\"...\"},{\"variation\":2,\"content\":\"...\"},{\"variation\":3,\"content\":\"...\"}]");
      const results=parseJ(raw);
      setCopyResults(results);
      setHistory(prev=>[{id:Date.now(),platform,tone:tn,bizName,timestamp:new Date().toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}),results},...prev.slice(0,19)]);
    } catch(e) { setCopyError(e.message||"Something went wrong."); }
    setCopyLoading(false);
  };

  const repurpose = async (content) => {
    setRepSource(content); setRepError(""); setRepLoading(true); setRepResults([]);
    try {
      const raw=await callClaude("You are an expert social media repurposing specialist.\nBusiness: "+(bizName||"a business")+" | Offer: "+(bizOffer||"our services")+"\nOriginal content: \""+content+"\"\nRewrite for ALL 4 platforms. Return ONLY valid JSON, no markdown:\n[{\"platform\":\"instagram\",\"content\":\"caption with emojis and 5 hashtags\"},{\"platform\":\"twitter\",\"content\":\"max 280 chars\"},{\"platform\":\"whatsapp\",\"content\":\"80-150 words broadcast\"},{\"platform\":\"product\",\"content\":\"3-5 sentence description\"}]");
      setRepResults(parseJ(raw));
    } catch(e) { setRepError(e.message||"Something went wrong."); }
    setRepLoading(false);
  };

  const suggestPrompt = async () => {
    if (!bizName.trim()&&!bizOffer.trim()) { setImgError("Fill in business info above first."); return; }
    setSugLoading(true); setImgError("");
    try {
      const raw=await callClaude("Write a concise image generation prompt (max 20 words) for a "+imgStyle+" style marketing visual.\nBusiness: "+(bizName||"a business")+" | Offer: "+(bizOffer||bizName)+"\nReturn ONLY the prompt text, nothing else.");
      setImgPrompt(raw.trim());
    } catch(e) { setImgError(e.message||"Could not suggest."); }
    setSugLoading(false);
  };

  const generateImages = () => {
    if (!imgPrompt.trim()) { setImgError("Enter or generate a prompt first."); return; }
    setImgError(""); setImgLoading(true); setImages([]); setImgLoaded([]);
    const sg=IMG_STYLES.find(s=>s.id===imgStyle)?.guide||"";
    const full=imgPrompt+", "+sg+", marketing visual, high quality";
    setImages([1,2,3].map(()=>({url:"https://image.pollinations.ai/prompt/"+encodeURIComponent(full)+"?width=512&height=512&seed="+Math.floor(Math.random()*99999)+"&nologo=true",error:false})));
  };

  const handleImgLoad=useCallback((i)=>{
    setImgLoaded(prev=>{ const u=[...prev,i]; if(u.length>=3) setImgLoading(false); return u; });
  },[]);
  const handleImgError=useCallback((i)=>{
    setImages(prev=>prev.map((img,idx)=>idx===i?{...img,error:true}:img));
    setImgLoaded(prev=>{ const u=[...prev,i]; if(u.length>=3) setImgLoading(false); return u; });
  },[]);

  const generateScript = async () => {
    if (!bizName.trim()||!bizOffer.trim()) { setVideoError("Fill in business info above first."); return; }
    setVideoError(""); setVideoLoading(true); setVideoScript(null);
    const vt=VIDEO_TYPES.find(v=>v.id===videoType);
    try {
      const raw=await callClaude("You are a professional video scriptwriter.\nBusiness: "+bizName+" | Industry: "+(bizNiche||"General")+" | Offer: "+bizOffer+"\nVideo type: "+vt.label+" ("+vt.dur+") | Tone: "+TONE_GUIDES[videoTone]+"\nReturn ONLY valid JSON, no markdown:\n{\"hook\":\"opening line\",\"scenes\":[{\"scene\":1,\"duration\":\"3s\",\"visual\":\"what to show\",\"voiceover\":\"what to say\"},{\"scene\":2,\"duration\":\"5s\",\"visual\":\"...\",\"voiceover\":\"...\"},{\"scene\":3,\"duration\":\"5s\",\"visual\":\"...\",\"voiceover\":\"...\"},{\"scene\":4,\"duration\":\"5s\",\"visual\":\"...\",\"voiceover\":\"...\"},{\"scene\":5,\"duration\":\"5s\",\"visual\":\"...\",\"voiceover\":\"...\"}],\"cta\":\"final call to action\",\"caption\":\"ready-to-post caption\",\"hashtags\":\"#tag1 #tag2 #tag3 #tag4 #tag5\"}");
      setVideoScript(parseJ(raw));
    } catch(e) { setVideoError(e.message||"Something went wrong."); }
    setVideoLoading(false);
  };

  const copyFullScript = () => {
    if (!videoScript) return;
    const scenes=(videoScript.scenes||[]).map(s=>"Scene "+s.scene+" ("+s.duration+")\nVisual: "+s.visual+"\nVO: "+s.voiceover).join("\n\n");
    navigator.clipboard.writeText("SCRIPT — "+bizName+"\n\nHOOK: "+videoScript.hook+"\n\nSCENES:\n"+scenes+"\n\nCTA: "+videoScript.cta+"\n\nCAPTION:\n"+videoScript.caption+"\n"+videoScript.hashtags);
    setScriptCopied(true); setTimeout(()=>setScriptCopied(false),2000);
  };

  const scanTrends = async () => {
    if (!bizNiche.trim()&&!bizName.trim()) { setTrendError("Fill in Business Name and Industry above first."); return; }
    setTrendError(""); setTrendLoading(true); setTrendData(null); setTrendContent({});
    try {
      const raw=await callClaude("You are a social media trend analyst. Analyze current trends for this niche on "+trendPlatform+" as of early 2026.\nBusiness: "+(bizName||"a business")+" | Niche: "+(bizNiche||"general business")+"\nReturn ONLY valid JSON, no markdown:\n{\"summary\":\"2-sentence overview\",\"trends\":[{\"id\":1,\"title\":\"trend name\",\"emoji\":\"emoji\",\"why\":\"why trending\",\"opportunity\":\"how to use it\",\"heat\":\"hot\"},{\"id\":2,\"title\":\"...\",\"emoji\":\"...\",\"why\":\"...\",\"opportunity\":\"...\",\"heat\":\"warm\"},{\"id\":3,\"title\":\"...\",\"emoji\":\"...\",\"why\":\"...\",\"opportunity\":\"...\",\"heat\":\"rising\"},{\"id\":4,\"title\":\"...\",\"emoji\":\"...\",\"why\":\"...\",\"opportunity\":\"...\",\"heat\":\"warm\"},{\"id\":5,\"title\":\"...\",\"emoji\":\"...\",\"why\":\"...\",\"opportunity\":\"...\",\"heat\":\"rising\"}],\"patterns\":[{\"label\":\"Best posting time\",\"value\":\"...\"},{\"label\":\"Top content format\",\"value\":\"...\"},{\"label\":\"Audience pain point\",\"value\":\"...\"},{\"label\":\"Viral hook style\",\"value\":\"...\"}],\"hashtags\":[\"#tag1\",\"#tag2\",\"#tag3\",\"#tag4\",\"#tag5\",\"#tag6\",\"#tag7\",\"#tag8\"]}",1500);
      setTrendData(parseJ(raw));
    } catch(e) { setTrendError(e.message||"Could not load trends."); }
    setTrendLoading(false);
  };

  const generateTrendContent = async (trend) => {
    setTrendCL(trend.id);
    try {
      const raw=await callClaude("Expert social media copywriter.\nBusiness: "+bizName+" | Niche: "+bizNiche+" | Offer: "+bizOffer+"\nPlatform: "+trendPlatform+" | Trend: \""+trend.title+"\" — "+trend.why+"\nOpportunity: "+trend.opportunity+"\nWrite 2 posts tapping into this trend. Return ONLY valid JSON, no markdown:\n[{\"post\":1,\"content\":\"...\"},{\"post\":2,\"content\":\"...\"}]");
      setTrendContent(prev=>({...prev,[trend.id]:parseJ(raw)}));
    } catch(e) { setTrendError(e.message||"Could not generate posts."); }
    setTrendCL(null);
  };

  const generateCalendar = async () => {
    if (!bizName.trim()||!bizOffer.trim()) { setCalError("Fill in Business Name and Product/Service above first."); return; }
    setCalError(""); setCalLoading(true); setCalendar(null);
    try {
      const raw=await callClaude("You are a social media content strategist.\nBusiness: "+bizName+" | Industry: "+(bizNiche||"General")+" | Offer: "+bizOffer+"\nCreate a 7-day content calendar Monday to Sunday. Vary platforms, tones, content types.\nReturn ONLY valid JSON, no markdown:\n{\"week\":[{\"day\":\"Monday\",\"platform\":\"Instagram\",\"type\":\"Educational\",\"hook\":\"...\",\"content\":\"...\",\"hashtags\":\"#tag1 #tag2 #tag3\"},{\"day\":\"Tuesday\",\"platform\":\"Twitter/X\",\"type\":\"Engagement\",\"hook\":\"...\",\"content\":\"...\",\"hashtags\":\"#tag1 #tag2\"},{\"day\":\"Wednesday\",\"platform\":\"WhatsApp\",\"type\":\"Promotional\",\"hook\":\"...\",\"content\":\"...\",\"hashtags\":\"\"},{\"day\":\"Thursday\",\"platform\":\"Instagram\",\"type\":\"Behind the Scenes\",\"hook\":\"...\",\"content\":\"...\",\"hashtags\":\"#tag1 #tag2 #tag3\"},{\"day\":\"Friday\",\"platform\":\"Twitter/X\",\"type\":\"Social Proof\",\"hook\":\"...\",\"content\":\"...\",\"hashtags\":\"#tag1 #tag2\"},{\"day\":\"Saturday\",\"platform\":\"Instagram\",\"type\":\"Entertainment\",\"hook\":\"...\",\"content\":\"...\",\"hashtags\":\"#tag1 #tag2 #tag3 #tag4\"},{\"day\":\"Sunday\",\"platform\":\"WhatsApp\",\"type\":\"Weekly Recap\",\"hook\":\"...\",\"content\":\"...\",\"hashtags\":\"\"}]}",2000);
      setCalendar(parseJ(raw));
    } catch(e) { setCalError(e.message||"Something went wrong."); }
    setCalLoading(false);
  };

  const copyCalDay = (day) => {
    navigator.clipboard.writeText(day.day+" — "+day.platform+"\n"+day.type+"\n\n"+day.hook+"\n\n"+day.content+"\n\n"+day.hashtags);
    setCalCopied(day.day); setTimeout(()=>setCalCopied(null),2000);
  };

  const copyFullCal = () => {
    if (!calendar) return;
    navigator.clipboard.writeText("WEEKLY CALENDAR — "+bizName+"\n\n"+calendar.week.map(d=>"=== "+d.day.toUpperCase()+" — "+d.platform+" ===\nType: "+d.type+"\nHook: "+d.hook+"\n\n"+d.content+"\n"+d.hashtags).join("\n\n"));
    setCalCopied("all"); setTimeout(()=>setCalCopied(null),2000);
  };

  const rewriteCompetitor = async () => {
    if (!compPost.trim()) { setCompError("Paste a competitor post first."); return; }
    if (!bizName.trim()) { setCompError("Fill in your Business Name above first."); return; }
    setCompError(""); setCompLoading(true); setCompResults(null);
    try {
      const raw=await callClaude("You are an expert social media strategist.\nMy Business: "+bizName+" | Industry: "+(bizNiche||"General")+" | Offer: "+(bizOffer||"our services")+"\nCompetitor post:\n\""+compPost+"\"\n1. Identify what makes it work\n2. Rewrite it 3 times — better, for MY business, different angles\nReturn ONLY valid JSON, no markdown:\n{\"analysis\":\"what makes the competitor post effective (2 sentences)\",\"rewrites\":[{\"angle\":\"...\",\"content\":\"...\"},{\"angle\":\"...\",\"content\":\"...\"},{\"angle\":\"...\",\"content\":\"...\"}]}");
      setCompResults(parseJ(raw));
    } catch(e) { setCompError(e.message||"Something went wrong."); }
    setCompLoading(false);
  };

  const hC={hot:C.accent,warm:C.amber,rising:C.green};
  const hL={hot:"🔥 Hot",warm:"♨️ Warm",rising:"📈 Rising"};
  const pColor={"Instagram":"#E1306C","Twitter/X":"#1DA1F2","WhatsApp":"#25D366","LinkedIn":"#0077B5"};
  const dColors=["#FF5C2B","#6366f1","#0ea5e9","#f59e0b","#10b981","#ec4899","#8b5cf6"];

  return (
    <div style={{fontFamily:"'Segoe UI',sans-serif",background:C.bg,minHeight:"100vh",padding:"22px 18px 80px"}}>
      <div style={{maxWidth:720,margin:"0 auto"}}>

        <div style={{marginBottom:16,display:"flex",alignItems:"flex-start",justifyContent:"space-between"}}>
          <div>
            <div style={{fontSize:"1.4rem",fontWeight:800,color:C.dark}}>
              Content<span style={{color:C.accent}}>AI</span>
              <span style={{fontSize:"0.5rem",background:C.accent,color:"white",padding:"2px 7px",borderRadius:10,fontWeight:700,marginLeft:8,verticalAlign:"middle"}}>STUDIO v2</span>
            </div>
            <div style={{fontSize:"0.72rem",color:C.muted}}>Copy · Images · Scripts · Trends · Calendar · Competitor</div>
          </div>
          {history.length>0&&(
            <button onClick={()=>setShowHistory(!showHistory)}
              style={{fontSize:"0.66rem",background:showHistory?C.dark:C.tag,border:"none",color:showHistory?"white":C.muted,padding:"5px 12px",borderRadius:8,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>
              🕒 History ({history.length})
            </button>
          )}
        </div>

        {showHistory&&history.length>0&&(
          <div style={{background:C.surface,border:"1.5px solid "+C.border,borderRadius:12,padding:14,marginBottom:16,maxHeight:260,overflowY:"auto"}}>
            <div style={{fontSize:"0.6rem",fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:10}}>History (this session)</div>
            {history.map((h,hi)=>(
              <div key={h.id} style={{borderBottom:"1px solid "+C.border,paddingBottom:8,marginBottom:8}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                  <span style={{fontSize:"0.65rem",fontWeight:700,color:C.dark}}>{h.bizName} — {PLATFORMS.find(p=>p.id===h.platform)?.label}</span>
                  <span style={{fontSize:"0.6rem",color:C.muted}}>{h.timestamp}</span>
                </div>
                {h.results.map((r,ri)=>(
                  <div key={ri} style={{fontSize:"0.72rem",color:C.muted,marginBottom:3,display:"flex",gap:8,alignItems:"center"}}>
                    <span style={{color:C.accent,fontWeight:700,flexShrink:0}}>V{ri+1}</span>
                    <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.content}</span>
                    <button onClick={()=>cp("h"+hi+ri,r.content)} style={{flexShrink:0,fontSize:"0.58rem",background:copied==="h"+hi+ri?C.green:C.tag,border:"none",color:copied==="h"+hi+ri?"white":C.muted,padding:"2px 7px",borderRadius:5,cursor:"pointer",fontFamily:"inherit"}}>
                      {copied==="h"+hi+ri?"✓":"Copy"}
                    </button>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        <div style={{background:C.surface,border:"1px solid "+C.border,borderRadius:12,padding:14,marginBottom:16}}>
          <span style={slabel}>Your Business</span>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
            <div>
              <label style={{fontSize:"0.66rem",color:C.muted,marginBottom:3,display:"block"}}>Business Name</label>
              <input style={inp} value={bizName} onChange={e=>setBizName(e.target.value)} placeholder="e.g. JERRYTECH"/>
            </div>
            <div>
              <label style={{fontSize:"0.66rem",color:C.muted,marginBottom:3,display:"block"}}>Industry / Niche</label>
              <input style={inp} value={bizNiche} onChange={e=>setBizNiche(e.target.value)} placeholder="e.g. AI Automation"/>
            </div>
          </div>
          <label style={{fontSize:"0.66rem",color:C.muted,marginBottom:3,display:"block"}}>Product / Service / Offer</label>
          <textarea value={bizOffer} onChange={e=>setBizOffer(e.target.value)} rows={2}
            placeholder="e.g. We automate repetitive tasks so businesses save time"
            style={{...inp,resize:"none",lineHeight:1.5}}/>
        </div>

        <div style={{display:"flex",gap:4,background:C.tag,borderRadius:10,padding:4,marginBottom:18}}>
          {[["copy","✍️ Copy"],["images","🖼️ Images"],["video","🎬 Script"],["trends","📊 Trends"],["calendar","📅 Calendar"],["competitor","🕵️ Rival"]].map(([id,label])=>(
            <button key={id} style={tabS(tab===id)} onClick={()=>setTab(id)}>{label}</button>
          ))}
        </div>

        {tab==="copy"&&(
          <>
            <span style={slabel}>Platform</span>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:14}}>
              {PLATFORMS.map(p=><button key={p.id} style={chip(platform===p.id)} onClick={()=>setPlatform(p.id)}>{p.label}</button>)}
            </div>
            <span style={slabel}>Tone</span>
            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:18}}>
              {TONES.map(t=><button key={t.id} style={tone(tn===t.id)} onClick={()=>setTn(t.id)}>{t.label}</button>)}
            </div>
            <button style={bigBtn(copyLoading)} onClick={generateCopy} disabled={copyLoading}>{copyLoading?"⏳ Generating...":"✦ Generate 3 Variations"}</button>
            {copyError&&<ErrBox msg={copyError}/>}
            {copyLoading&&<Dots text="Writing your variations..."/>}
            {copyResults.length>0&&(
              <div style={{marginTop:20}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                  <span style={{fontWeight:700,color:C.dark}}>Variations</span>
                  <button onClick={generateCopy} style={{fontSize:"0.66rem",color:C.accent,border:"1.5px solid "+C.accent,background:"none",borderRadius:6,padding:"3px 10px",cursor:"pointer",fontFamily:"inherit"}}>↻ Redo</button>
                </div>
                {copyResults.map((v,i)=>(
                  <div key={i} style={{...card,borderLeft:"3px solid "+C.accent}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                      <span style={{fontSize:"0.58rem",fontWeight:700,color:C.muted,textTransform:"uppercase"}}>V{i+1}</span>
                      <div style={{display:"flex",gap:6}}>
                        <button onClick={()=>repurpose(v.content)} style={{fontSize:"0.6rem",background:C.tag,border:"none",color:C.muted,padding:"3px 8px",borderRadius:6,cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>↗ Repurpose</button>
                        <button onClick={()=>cp(i,v.content)} style={{fontSize:"0.62rem",background:copied===i?C.green:C.tag,border:"none",color:copied===i?"white":C.muted,padding:"3px 10px",borderRadius:6,cursor:"pointer",fontFamily:"inherit"}}>
                          {copied===i?"Copied!":"Copy"}
                        </button>
                      </div>
                    </div>
                    <div style={{fontSize:"0.82rem",lineHeight:1.7,color:C.dark,whiteSpace:"pre-wrap",wordBreak:"break-word"}}>{v.content}</div>
                    <CharCount text={v.content} limit={PLATFORMS.find(p=>p.id===platform)?.limit||2200}/>
                  </div>
                ))}
              </div>
            )}
            {repSource&&(
              <div style={{marginTop:24}}>
                <div style={{...card,background:"#FFF3EE",border:"1.5px solid "+C.accent+"44"}}>
                  <div style={{fontSize:"0.6rem",fontWeight:700,color:C.accent,textTransform:"uppercase",marginBottom:4}}>↗ Repurposing</div>
                  <div style={{fontSize:"0.76rem",color:C.dark,fontStyle:"italic"}}>"{repSource.slice(0,100)}{repSource.length>100?"...":""}"</div>
                </div>
                {repError&&<ErrBox msg={repError}/>}
                {repLoading&&<Dots text="Repurposing across all platforms..."/>}
                {repResults.length>0&&(
                  <>
                    <div style={{fontWeight:700,color:C.dark,marginBottom:10,fontSize:"0.88rem"}}>Repurposed for All Platforms</div>
                    {repResults.map((r,i)=>{
                      const pl=PLATFORMS.find(p=>p.id===r.platform);
                      return (
                        <div key={i} style={{...card,borderLeft:"3px solid "+C.accent}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                            <span style={{fontSize:"0.62rem",fontWeight:700,color:C.muted,textTransform:"uppercase"}}>{pl?.label||r.platform}</span>
                            <button onClick={()=>cp("rep"+i,r.content)} style={{fontSize:"0.6rem",background:copied==="rep"+i?C.green:C.tag,border:"none",color:copied==="rep"+i?"white":C.muted,padding:"3px 9px",borderRadius:6,cursor:"pointer",fontFamily:"inherit"}}>
                              {copied==="rep"+i?"Copied!":"Copy"}
                            </button>
                          </div>
                          <div style={{fontSize:"0.8rem",lineHeight:1.7,color:C.dark,whiteSpace:"pre-wrap"}}>{r.content}</div>
                          <CharCount text={r.content} limit={pl?.limit||2200}/>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </>
        )}

        {tab==="images"&&(
          <>
            <span style={slabel}>Image Style</span>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:16}}>
              {IMG_STYLES.map(s=><button key={s.id} style={chip(imgStyle===s.id)} onClick={()=>setImgStyle(s.id)}>{s.label}</button>)}
            </div>
            <span style={slabel}>Image Prompt</span>
            <textarea value={imgPrompt} onChange={e=>setImgPrompt(e.target.value)} rows={3}
              placeholder="Describe the image... or click AI Suggest below"
              style={{...inp,resize:"none",lineHeight:1.5,marginBottom:10}}/>
            <button onClick={suggestPrompt} disabled={sugLoading}
              style={{width:"100%",padding:10,borderRadius:10,background:"transparent",border:"1.5px dashed "+C.accent,color:C.accent,fontSize:"0.78rem",fontWeight:600,cursor:sugLoading?"not-allowed":"pointer",fontFamily:"inherit",marginBottom:10,opacity:sugLoading?0.6:1}}>
              {sugLoading?"✨ Thinking...":"✨ AI Suggest a Prompt for My Business"}
            </button>
            <button style={bigBtn(imgLoading)} onClick={generateImages} disabled={imgLoading}>
              {imgLoading?"⏳ Loading images ("+imgLoaded.length+"/3)...":"🖼️ Generate 3 Images (Free)"}
            </button>
            {imgError&&<ErrBox msg={imgError}/>}
            {images.length>0&&(
              <div style={{marginTop:20}}>
                <div style={{fontWeight:700,color:C.dark,marginBottom:12}}>Generated Images {imgLoading&&<span style={{fontSize:"0.7rem",color:C.muted,fontWeight:400}}>— loading...</span>}</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
                  {images.map((img,i)=>(
                    <div key={i} style={{borderRadius:12,overflow:"hidden",border:"1.5px solid "+C.border,background:C.tag}}>
                      {img.error
                        ?<div style={{aspectRatio:"1",display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:4,color:C.muted,fontSize:"0.7rem"}}><span>⚠️</span><span>Failed to load</span></div>
                        :<img src={img.url} alt={"v"+(i+1)} onLoad={()=>handleImgLoad(i)} onError={()=>handleImgError(i)}
                            style={{width:"100%",aspectRatio:"1",objectFit:"cover",display:"block",opacity:imgLoaded.includes(i)?1:0.3,transition:"opacity 0.3s"}}/>
                      }
                      <div style={{padding:"7px 10px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <span style={{fontSize:"0.58rem",color:C.muted}}>V{i+1}</span>
                        <button onClick={()=>window.open(img.url,"_blank")} style={{fontSize:"0.6rem",background:C.dark,border:"none",color:"white",padding:"3px 8px",borderRadius:6,cursor:"pointer",fontFamily:"inherit"}}>↓ Open</button>
                      </div>
                    </div>
                  ))}
                </div>
                <button onClick={generateImages} style={{marginTop:10,width:"100%",padding:9,borderRadius:10,background:"transparent",border:"1.5px dashed "+C.border,color:C.muted,fontSize:"0.74rem",cursor:"pointer",fontFamily:"inherit"}}>↻ Regenerate</button>
              </div>
            )}
          </>
        )}

        {tab==="video"&&(
          <>
            <span style={slabel}>Video Type</span>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:16}}>
              {VIDEO_TYPES.map(v=>(
                <button key={v.id} style={chip(videoType===v.id)} onClick={()=>setVideoType(v.id)}>
                  <div>{v.label}</div><div style={{fontSize:"0.56rem",opacity:0.7}}>{v.dur}</div>
                </button>
              ))}
            </div>
            <span style={slabel}>Tone</span>
            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:20}}>
              {TONES.map(t=><button key={t.id} style={tone(videoTone===t.id)} onClick={()=>setVideoTone(t.id)}>{t.label}</button>)}
            </div>
            <button style={bigBtn(videoLoading)} onClick={generateScript} disabled={videoLoading}>{videoLoading?"⏳ Writing...":"🎬 Generate Video Script"}</button>
            {videoError&&<ErrBox msg={videoError}/>}
            {videoLoading&&<Dots text="Writing your script..."/>}
            {videoScript&&(
              <div style={{marginTop:20}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                  <span style={{fontWeight:700,color:C.dark}}>Your Script</span>
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={generateScript} style={{fontSize:"0.64rem",color:C.accent,border:"1.5px solid "+C.accent,background:"none",borderRadius:6,padding:"3px 10px",cursor:"pointer",fontFamily:"inherit"}}>↻ Redo</button>
                    <button onClick={copyFullScript} style={{fontSize:"0.64rem",background:scriptCopied?C.green:C.dark,border:"none",color:"white",borderRadius:6,padding:"3px 12px",cursor:"pointer",fontFamily:"inherit"}}>{scriptCopied?"Copied!":"Copy All"}</button>
                  </div>
                </div>
                <div style={{background:"#FFF3EE",border:"2px solid "+C.accent,borderRadius:12,padding:14,marginBottom:10}}>
                  <div style={{fontSize:"0.58rem",fontWeight:700,color:C.accent,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:6}}>🪝 Hook</div>
                  <div style={{fontSize:"0.9rem",fontWeight:700,color:C.dark}}>"{videoScript.hook}"</div>
                </div>
                {(videoScript.scenes||[]).map((sc,i)=>(
                  <div key={i} style={{...card,display:"grid",gridTemplateColumns:"34px 1fr",gap:10}}>
                    <div style={{background:C.tag,borderRadius:8,width:34,height:34,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
                      <span style={{fontSize:"0.58rem",fontWeight:700,color:C.muted}}>S{sc.scene}</span>
                      <span style={{fontSize:"0.5rem",color:C.muted}}>{sc.duration}</span>
                    </div>
                    <div>
                      <div style={{fontSize:"0.66rem",color:C.muted,marginBottom:3}}>📽 {sc.visual}</div>
                      <div style={{fontSize:"0.8rem",color:C.dark,lineHeight:1.5}}>🎙 {sc.voiceover}</div>
                    </div>
                  </div>
                ))}
                <div style={{...card,marginBottom:8}}>
                  <div style={{fontSize:"0.58rem",fontWeight:700,color:C.muted,textTransform:"uppercase",marginBottom:4}}>📢 CTA</div>
                  <div style={{fontSize:"0.84rem",color:C.dark}}>{videoScript.cta}</div>
                </div>
                <div style={card}>
                  <div style={{fontSize:"0.58rem",fontWeight:700,color:C.muted,textTransform:"uppercase",marginBottom:4}}>📝 Caption</div>
                  <div style={{fontSize:"0.82rem",color:C.dark,lineHeight:1.6,whiteSpace:"pre-wrap"}}>{videoScript.caption}</div>
                  <div style={{fontSize:"0.76rem",color:C.accent,marginTop:6}}>{videoScript.hashtags}</div>
                </div>
              </div>
            )}
          </>
        )}

        {tab==="trends"&&(
          <>
            <div style={{background:"#FFF3EE",border:"1.5px solid "+C.accent+"33",borderRadius:12,padding:12,marginBottom:16,fontSize:"0.76rem",color:C.dark,lineHeight:1.6}}>
              📊 AI scans trends in your niche, shows posting patterns, then generates ready-to-post content from any trend in one click.
            </div>
            <span style={slabel}>Scan Platform</span>
            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:18}}>
              {TREND_PLATFORMS.map(p=><button key={p.id} style={chip(trendPlatform===p.id)} onClick={()=>setTrendPlatform(p.id)}>{p.label}</button>)}
            </div>
            <button style={bigBtn(trendLoading)} onClick={scanTrends} disabled={trendLoading}>
              {trendLoading?"⏳ Scanning...":"📊 Scan "+TREND_PLATFORMS.find(p=>p.id===trendPlatform)?.label+" Trends"}
            </button>
            {trendError&&<ErrBox msg={trendError}/>}
            {trendLoading&&<Dots text="Analyzing trends and patterns..."/>}
            {trendData&&(
              <div style={{marginTop:22}}>
                <div style={card}>
                  <div style={{fontSize:"0.58rem",fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:6}}>📣 Summary</div>
                  <div style={{fontSize:"0.84rem",color:C.dark,lineHeight:1.6}}>{trendData.summary}</div>
                </div>
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:"0.58rem",fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:10}}>🧩 Content Patterns</div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                    {trendData.patterns?.map((p,i)=>(
                      <div key={i} style={{background:C.surface,border:"1.5px solid "+C.border,borderRadius:10,padding:12}}>
                        <div style={{fontSize:"0.6rem",color:C.muted,marginBottom:3}}>{p.label}</div>
                        <div style={{fontSize:"0.8rem",fontWeight:700,color:C.dark}}>{p.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:"0.58rem",fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:8}}>🏷️ Trending Hashtags</div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {trendData.hashtags?.map((h,i)=>(
                      <span key={i} onClick={()=>navigator.clipboard.writeText(h)}
                        style={{background:C.tag,color:C.dark,fontSize:"0.72rem",padding:"4px 10px",borderRadius:20,cursor:"pointer"}}>{h}</span>
                    ))}
                  </div>
                </div>
                <div style={{fontSize:"0.58rem",fontWeight:700,color:C.muted,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:10}}>🔥 Top Trends</div>
                {trendData.trends?.map((trend)=>(
                  <div key={trend.id} style={card}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                      <span style={{fontSize:"1rem"}}>{trend.emoji}</span>
                      <span style={{fontWeight:700,color:C.dark,fontSize:"0.88rem"}}>{trend.title}</span>
                      <span style={{fontSize:"0.6rem",fontWeight:700,color:hC[trend.heat]||C.accent,background:(hC[trend.heat]||C.accent)+"18",padding:"2px 7px",borderRadius:10}}>
                        {hL[trend.heat]||"📈"}
                      </span>
                    </div>
                    <div style={{fontSize:"0.74rem",color:C.muted,marginBottom:3}}>📌 {trend.why}</div>
                    <div style={{fontSize:"0.74rem",color:C.dark,marginBottom:10}}>💡 {trend.opportunity}</div>
                    <button onClick={()=>generateTrendContent(trend)} disabled={trendCL===trend.id}
                      style={{width:"100%",padding:8,borderRadius:8,background:trendCL===trend.id?C.tag:C.dark,border:"none",color:trendCL===trend.id?C.muted:"white",fontSize:"0.72rem",fontWeight:700,cursor:trendCL===trend.id?"not-allowed":"pointer",fontFamily:"inherit"}}>
                      {trendCL===trend.id?"⏳ Writing posts...":"✦ Generate Posts for This Trend"}
                    </button>
                    {trendContent[trend.id]&&(
                      <div style={{marginTop:10}}>
                        {trendContent[trend.id].map((p,pi)=>(
                          <div key={pi} style={{background:C.bg,border:"1px solid "+C.border,borderRadius:8,padding:12,marginBottom:6}}>
                            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                              <span style={{fontSize:"0.58rem",color:C.muted,fontWeight:600,textTransform:"uppercase"}}>Post {p.post}</span>
                              <button onClick={()=>{navigator.clipboard.writeText(p.content);setTrendCopied(trend.id+"-"+pi);setTimeout(()=>setTrendCopied(null),2000);}}
                                style={{fontSize:"0.6rem",background:trendCopied===trend.id+"-"+pi?C.green:C.tag,border:"none",color:trendCopied===trend.id+"-"+pi?"white":C.muted,padding:"2px 8px",borderRadius:6,cursor:"pointer",fontFamily:"inherit"}}>
                                {trendCopied===trend.id+"-"+pi?"Copied!":"Copy"}
                              </button>
                            </div>
                            <div style={{fontSize:"0.8rem",color:C.dark,lineHeight:1.6,whiteSpace:"pre-wrap"}}>{p.content}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <button onClick={scanTrends} style={{marginTop:8,width:"100%",padding:10,borderRadius:10,background:"transparent",border:"1.5px dashed "+C.border,color:C.muted,fontSize:"0.74rem",cursor:"pointer",fontFamily:"inherit"}}>↻ Rescan</button>
              </div>
            )}
          </>
        )}

        {tab==="calendar"&&(
          <>
            <div style={{background:"#F0FFF4",border:"1.5px solid #86efac",borderRadius:12,padding:12,marginBottom:16,fontSize:"0.76rem",color:C.dark,lineHeight:1.6}}>
              📅 Generate a full <strong>7-day content calendar</strong> in one click — different platform, content type, hook, post and hashtags for every day.
            </div>
            <button style={bigBtn(calLoading)} onClick={generateCalendar} disabled={calLoading}>
              {calLoading?"⏳ Building Calendar...":"📅 Generate This Week's Content Calendar"}
            </button>
            {calError&&<ErrBox msg={calError}/>}
            {calLoading&&<Dots text="Planning your entire week..."/>}
            {calendar&&(
              <div style={{marginTop:22}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                  <span style={{fontWeight:700,color:C.dark,fontSize:"0.95rem"}}>📅 This Week's Plan</span>
                  <button onClick={copyFullCal}
                    style={{fontSize:"0.68rem",background:calCopied==="all"?C.green:C.dark,border:"none",color:"white",borderRadius:6,padding:"4px 12px",cursor:"pointer",fontFamily:"inherit",fontWeight:600}}>
                    {calCopied==="all"?"Copied!":"Copy Full Calendar"}
                  </button>
                </div>
                {calendar.week?.map((day,i)=>(
                  <div key={i} style={{...card,borderLeft:"3px solid "+dColors[i]}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                      <div>
                        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
                          <span style={{fontWeight:700,color:C.dark,fontSize:"0.9rem"}}>{day.day}</span>
                          <span style={{fontSize:"0.62rem",background:(pColor[day.platform]||C.accent)+"18",color:pColor[day.platform]||C.accent,padding:"2px 8px",borderRadius:10,fontWeight:700}}>{day.platform}</span>
                          <span style={{fontSize:"0.6rem",background:C.tag,color:C.muted,padding:"2px 7px",borderRadius:10}}>{day.type}</span>
                        </div>
                        <div style={{fontSize:"0.76rem",fontWeight:700,color:C.dark,fontStyle:"italic"}}>"{day.hook}"</div>
                      </div>
                      <button onClick={()=>copyCalDay(day)}
                        style={{flexShrink:0,fontSize:"0.6rem",background:calCopied===day.day?C.green:C.tag,border:"none",color:calCopied===day.day?"white":C.muted,padding:"3px 9px",borderRadius:6,cursor:"pointer",fontFamily:"inherit",marginLeft:8}}>
                        {calCopied===day.day?"Copied!":"Copy"}
                      </button>
                    </div>
                    <div style={{fontSize:"0.8rem",color:C.dark,lineHeight:1.6,whiteSpace:"pre-wrap",marginBottom:6}}>{day.content}</div>
                    {day.hashtags&&<div style={{fontSize:"0.72rem",color:C.accent}}>{day.hashtags}</div>}
                  </div>
                ))}
                <button onClick={generateCalendar} style={{marginTop:8,width:"100%",padding:10,borderRadius:10,background:"transparent",border:"1.5px dashed "+C.border,color:C.muted,fontSize:"0.74rem",cursor:"pointer",fontFamily:"inherit"}}>↻ Regenerate</button>
              </div>
            )}
          </>
        )}

        {tab==="competitor"&&(
          <>
            <div style={{background:"#FFF8F0",border:"1.5px solid "+C.amber+"44",borderRadius:12,padding:12,marginBottom:16,fontSize:"0.76rem",color:C.dark,lineHeight:1.6}}>
              🕵️ Paste any competitor's post. AI will <strong>analyze what makes it work</strong> then rewrite it 3 times — better, for your brand, with different angles.
            </div>
            <span style={slabel}>Competitor's Post</span>
            <textarea value={compPost} onChange={e=>setCompPost(e.target.value)} rows={5}
              placeholder="Paste your competitor's post here..."
              style={{...inp,resize:"none",lineHeight:1.5,marginBottom:12}}/>
            <button style={bigBtn(compLoading)} onClick={rewriteCompetitor} disabled={compLoading}>
              {compLoading?"⏳ Analyzing & Rewriting...":"🕵️ Analyze & Beat This Post"}
            </button>
            {compError&&<ErrBox msg={compError}/>}
            {compLoading&&<Dots text="Studying their post and writing yours better..."/>}
            {compResults&&compResults.analysis&&(
              <div style={{marginTop:22}}>
                <div style={{background:"#FFF8F0",border:"1.5px solid "+C.amber+"44",borderRadius:12,padding:14,marginBottom:14}}>
                  <div style={{fontSize:"0.6rem",fontWeight:700,color:C.amber,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:6}}>🔍 Why Their Post Works</div>
                  <div style={{fontSize:"0.84rem",color:C.dark,lineHeight:1.6}}>{compResults.analysis}</div>
                </div>
                <div style={{fontWeight:700,color:C.dark,marginBottom:12,fontSize:"0.9rem"}}>Your Better Versions</div>
                {compResults.rewrites?.map((r,i)=>(
                  <div key={i} style={{...card,borderLeft:"3px solid "+C.accent}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                      <span style={{fontSize:"0.65rem",fontWeight:700,color:C.accent,background:C.accent+"14",padding:"2px 8px",borderRadius:8}}>Angle: {r.angle}</span>
                      <button onClick={()=>{navigator.clipboard.writeText(r.content);setCompCopied(i);setTimeout(()=>setCompCopied(null),2000);}}
                        style={{fontSize:"0.62rem",background:compCopied===i?C.green:C.tag,border:"none",color:compCopied===i?"white":C.muted,padding:"3px 10px",borderRadius:6,cursor:"pointer",fontFamily:"inherit"}}>
                        {compCopied===i?"Copied!":"Copy"}
                      </button>
                    </div>
                    <div style={{fontSize:"0.82rem",lineHeight:1.7,color:C.dark,whiteSpace:"pre-wrap"}}>{r.content}</div>
                  </div>
                ))}
                <button onClick={rewriteCompetitor} style={{marginTop:8,width:"100%",padding:10,borderRadius:10,background:"transparent",border:"1.5px dashed "+C.border,color:C.muted,fontSize:"0.74rem",cursor:"pointer",fontFamily:"inherit"}}>↻ Regenerate</button>
              </div>
            )}
          </>
        )}

      </div>
      <style>{"@keyframes bounce{0%,80%,100%{transform:translateY(0);opacity:0.4}40%{transform:translateY(-8px);opacity:1}}"}</style>
    </div>
  );
}
